import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, CheckCircle2, AlertCircle, RefreshCw, Loader2, XCircle } from 'lucide-react';
import { useAuth } from '@/stores/authStore';
import { toast } from '@/stores/toastStore';
import api from '@/services/api';
import Button from '@/components/common/Button';
import { useTranslation } from 'react-i18next';

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const { t } = useTranslation('auth');

  // Auto-verify when token is present in URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      verifyWithToken(token);
    }
  }, [searchParams]);

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const goToProfile = () => {
    if (user?.role === 'CANDIDATE') {
      navigate('/candidate/dashboard');
    } else if (user?.role === 'RECRUITER') {
      navigate('/recruiter/dashboard');
    } else {
      navigate('/');
    }
  };

  const verifyWithToken = async (token: string) => {
    setIsVerifying(true);
    setVerificationError(null);
    try {
      const response = await api.post('/auth/verify-email', { token });
      // Handle case where API returns 200 with EMAIL_ALREADY_VERIFIED in body
      if (response.data?.code === 'EMAIL_ALREADY_VERIFIED') {
        toast.success(t('verifyEmail.alreadyVerifiedSuccess'));
      } else {
        toast.success(t('verifyEmail.verifiedSuccess'));
      }
      setIsVerified(true);
    } catch (error: any) {
      const data = error.response?.data;
      if (data?.code === 'EMAIL_ALREADY_VERIFIED') {
        setIsVerified(true);
        toast.success(t('verifyEmail.alreadyVerifiedSuccess'));
      } else {
        const errorMessage =
          (typeof data?.message === 'string' ? data.message : null) ||
          (typeof data?.error === 'string' ? data.error : null) ||
          'Verification failed. The link may be invalid or expired.';
        setVerificationError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const resendVerificationEmail = async () => {
    setIsResending(true);
    try {
      await api.post('/auth/send-verification');
      toast.success(t('verifyEmail.successMessage'));
      setCountdown(60);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to resend email. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  const pageWrapper = "min-h-screen w-full flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-white to-purple-50/60 fixed inset-0 overflow-auto";
  const card = "bg-white/70 backdrop-blur-sm p-8 rounded-2xl border border-gray-200 shadow-lg text-center animate-scale-in";

  // Success state
  if (isVerified) {
    return (
      <div className={pageWrapper}>
        <div className="w-full max-w-md">
          <div className={card}>
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('verifyEmail.verifiedTitle')}</h1>
            <p className="text-gray-600 mb-8">{t('verifyEmail.verifiedMessage')}</p>
            <Button variant="primary" fullWidth onClick={goToProfile}>
              {t('verifyEmail.goToProfile')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Verifying token state (auto-verifying from URL)
  if (isVerifying) {
    return (
      <div className={pageWrapper}>
        <div className="w-full max-w-md">
          <div className={card}>
            <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-12 h-12 text-primary-600 animate-spin" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('verifyEmail.verifyingTitle')}</h1>
            <p className="text-gray-600">{t('verifyEmail.verifyingMessage')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state (token was present but verification failed)
  if (verificationError) {
    return (
      <div className={pageWrapper}>
        <div className="w-full max-w-md">
          <div className={card}>
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('verifyEmail.errorTitle')}</h1>
            <p className="text-gray-600 mb-3">{verificationError}</p>
            <p className="text-gray-500 text-sm mb-6">{t('verifyEmail.errorHint')}</p>
            <Button variant="primary" fullWidth onClick={goToProfile}>
              {t('verifyEmail.goToDashboard')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Default state: no token in URL — ask user to check email
  return (
    <div className={pageWrapper}>
      <div className="w-full max-w-md">
        {/* Title */}
        <div className="text-center mb-8 animate-slide-down">
          <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-primary-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('verifyEmail.title')}</h1>
          <p className="text-gray-500">
            {t('verifyEmail.subtitle')}{' '}
            <span className="font-semibold text-gray-900">{user?.email || t('verifyEmail.yourEmail')}</span>
          </p>
        </div>

        {/* Instructions Card */}
        <div className="bg-white/70 backdrop-blur-sm p-8 rounded-2xl border border-gray-200 shadow-lg animate-slide-up text-center">
          <p className="text-gray-700 mb-6">{t('verifyEmail.linkInstructions')}</p>

          <hr className="border-gray-200 mb-6" />

          {/* Resend Section */}
          <div className="text-center">
            <p className="text-gray-500 text-sm mb-3">{t('verifyEmail.didntReceive')}</p>
            <Button
              variant="secondary"
              onClick={resendVerificationEmail}
              disabled={countdown > 0 || isResending}
              fullWidth
            >
              {isResending ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  {t('verifyEmail.sending')}
                </>
              ) : countdown > 0 ? (
                <>{t('verifyEmail.resendIn')} {countdown}s</>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5 mr-2" />
                  {t('verifyEmail.resendButton')}
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Info Card */}
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 animate-slide-up">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="mb-1">
                <span className="font-semibold">{t('verifyEmail.checkSpamBold')}</span> {t('verifyEmail.checkSpam')}
              </p>
              <p>
                {t('verifyEmail.codeExpiry')} <span className="font-semibold">{t('verifyEmail.expiryTime')}</span>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
